/**
 * The browse shell: everything the canonical faceted-browse pages need around
 * the stock search block.
 *
 * Originally this only merged a `?preset=<id>` definition into the block's data
 * before the stock search hocs initialize. It now also supplies the things
 * those pages were missing entirely — the page header, category links,
 * crawlable pagination, and a self-referencing canonical/title — because all
 * of them hang off the same two facts (which family this block is, which preset
 * is active) and computing them twice would be how they drift.
 *
 * Composes OUTERMOST — `withPresetData(withNormalizedData(view))` — so the
 * merged criteria are normalized alongside the block's base query and are in
 * place before stock `withSearch` seeds its state from `data.query`.
 *
 * Acts ONLY on blocks carrying the `family` stamp written by
 * generate_canonical_browse_blocks.py; every other search/listing block (news,
 * contact-us, migrated variants) passes through untouched. The merge itself is
 * the pure, unit-tested `applyPreset`; this shell only reads location + site.
 *
 * The frame it renders is the SHARED one (item 15): `.pp-page` +
 * `<PageHeader>` + `<ResultsPager>` are the same components /search mounts, and
 * the only per-route element is the category strip. See theme/page.less.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useSiteConfig from '../helpers/useSiteConfig';
import { applyPreset, getPreset } from '../presets';
import BrowseIntro from './Browse/BrowseIntro';
import BrowseNav, { BrowseGroups } from './Browse/BrowseNav';
import BrowseJsonLd from './Browse/BrowseJsonLd';
import BrowseSEO, { measuredTotal } from './Browse/BrowseSEO';
import PageHeader from './Layout/PageHeader';
import ResultsPager from './Layout/ResultsPager';
import { pageFrom, pageKeyFor, presetIdFrom } from './Browse/browseState';

/**
 * What each container holds, in one sentence.
 *
 * Per FAMILY rather than per preset: the sentence describes the container, and
 * that stays true when a preset narrows it — a preset changes which rows you
 * see, not what kind of thing they are. Nineteen hand-written preset ledes
 * would be nineteen chances to say something that stops being true.
 */
const FAMILY_LEDE = {
  documents:
    'Every document in this archive — filter by category, status, office or date.',
  delegations:
    'Delegations and designations of authority — filter by type and status.',
  glossary:
    'Defined terms and acronyms used across the directives — filter by letter, type or office.',
  people: 'Programme contacts — filter by role and status.',
};

export default function withPresetData(Wrapped) {
  function Presetted(props) {
    const { id, properties: content } = props;
    const location = useLocation();
    const siteConfig = useSiteConfig();
    const family = props.data?.family;
    const preset = getPreset(
      siteConfig?.id,
      family,
      presetIdFrom(location?.search),
    );
    const pageKey = pageKeyFor(content, id);
    const currentPage = pageFrom(location?.search, pageKey);
    // Seeded server-side by components/Browse/getSearchBlockAsyncData; the
    // subrequest key for a search block is the bare block id (SearchBlockView
    // renders ListingBody without `properties`). A failed or still-pending
    // request is "not measured", never zero: see measuredTotal.
    const total = useSelector((state) =>
      measuredTotal(state.querystringsearch?.subrequests?.[id]),
    );

    if (!family) return <Wrapped {...props} />;

    const data = preset ? applyPreset(props.data, preset) : props.data;
    const bSize = data?.query?.b_size;
    const pathname = location?.pathname || '';

    return (
      <div className="pp-page pp-browse">
        <BrowseSEO
          site={siteConfig}
          family={family}
          preset={preset}
          content={content}
          pathname={pathname}
          pageKey={pageKey}
          currentPage={currentPage}
          total={total}
          bSize={bSize}
        />
        <BrowseJsonLd
          site={siteConfig}
          id={id}
          pathname={pathname}
          preset={preset}
        />
        {/* The active category names the page. The stock title block stands
            down for exactly this reason — it only ever knew the container's
            name, so `?preset=archives-browse` used to render an h1 saying
            "Documents" under a <title> saying "Archived Standards". */}
        <PageHeader
          title={preset?.label || content?.title || 'Browse'}
          lede={content?.description || FAMILY_LEDE[family]}
        />
        <BrowseIntro preset={preset} />
        <BrowseNav
          site={siteConfig}
          family={family}
          pathname={pathname}
          activePreset={preset?.id || null}
        />
        <Wrapped {...props} data={data} />
        <ResultsPager
          id={id}
          pathname={pathname}
          search={location?.search}
          pageKey={pageKey}
          currentPage={currentPage}
          bSize={bSize}
        />
        <BrowseGroups
          site={siteConfig}
          family={family}
          pathname={pathname}
          activePreset={preset?.id || null}
        />
      </div>
    );
  }
  Presetted.displayName = `withPresetData(${
    Wrapped?.displayName || Wrapped?.name || 'View'
  })`;
  Presetted.__ppPreset = true;
  return Presetted;
}
